// src/components/inputs/SetKeyboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InlineMath } from "react-katex";

function KeyboardIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="5.5" width="19" height="13" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <g fill="currentColor">
        {[[6, 9, 12, 15], [6, 9, 12, 15], [6, 9, 12, 15]].map((row, r) =>
          row.map((cx, i) => <circle key={`${r}-${i}`} cx={cx} cy={8 + r * 3.2} r="0.85" />)
        )}
        <rect x="7" y="14.8" width="10" height="1.3" rx="0.6" />
      </g>
    </svg>
  );
}

function HandwritingIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 17.5c3.5-3 5.5-3 7 0 1.3 2.6 3.3 2 5.5-.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14.5 5.5l4 4-8.2 8.2-4.7 1 1-4.7 7.9-8.5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M13.2 7l3.8 3.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function SpaceIcon({ width = 30, height = 14, stroke = "currentColor" }) {
  return (
    <svg width={width} height={height} viewBox="0 0 60 28" role="img" aria-hidden="true">
      <line x1="8" y1="18" x2="52" y2="18" stroke={stroke} strokeWidth="4" strokeLinecap="round" />
      <line x1="8" y1="10" x2="8" y2="18" stroke={stroke} strokeWidth="4" strokeLinecap="round" />
      <line x1="52" y1="10" x2="52" y2="18" stroke={stroke} strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

const anchorIds = new WeakMap();
let anchorCounter = 1;
const getAnchorId = (el) => {
  if (!el || typeof el !== "object") return undefined;
  if (!anchorIds.has(el)) anchorIds.set(el, `anchor-${anchorCounter++}`);
  return anchorIds.get(el);
};

const findAnchorElement = (rootEl) => {
  if (!rootEl) return undefined;
  const pick = rootEl.querySelector?.('input, textarea, [contenteditable="true"]');
  if (pick) return pick;
  const parent = rootEl.parentElement;
  if (parent) {
    const near = parent.querySelector?.('input, textarea, [contenteditable="true"]');
    if (near) return near;
  }
  return undefined;
};

const openRegistry = new Map();
const modeRegistry = new Map();
const MODE_EVENT = "matematron-setkeyboard-mode";
const getModeKey = (groupName, anchor) => `${groupName || "default"}:${anchor || "unknown"}`;

function getBounds(points) {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

function pathLength(points) {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return total;
}

function resample(points, count = 32) {
  if (!Array.isArray(points) || points.length < 2) return points || [];
  const interval = pathLength(points) / (count - 1);
  if (!Number.isFinite(interval) || interval <= 0) return points.slice(0, count);

  const result = [points[0]];
  let distance = 0;
  let source = points.slice();

  for (let i = 1; i < source.length; i += 1) {
    const currentDistance = Math.hypot(source[i].x - source[i - 1].x, source[i].y - source[i - 1].y);
    if (distance + currentDistance >= interval) {
      const ratio = (interval - distance) / currentDistance;
      const next = {
        x: source[i - 1].x + ratio * (source[i].x - source[i - 1].x),
        y: source[i - 1].y + ratio * (source[i].y - source[i - 1].y),
      };
      result.push(next);
      source.splice(i, 0, next);
      distance = 0;
    } else {
      distance += currentDistance;
    }
  }

  while (result.length < count) result.push(source[source.length - 1]);
  return result.slice(0, count);
}

function normalizePoints(points) {
  const b = getBounds(points);
  const width = Math.max(1, b.maxX - b.minX);
  const height = Math.max(1, b.maxY - b.minY);
  return points.map((p) => ({
    x: (p.x - b.minX) / width,
    y: (p.y - b.minY) / height,
  }));
}

function templateDistance(points, template) {
  const a = normalizePoints(resample(points));
  const b = normalizePoints(resample(template));
  if (!a.length || !b.length || a.length !== b.length) return Infinity;
  let sum = 0;
  for (let i = 0; i < a.length; i += 1) {
    sum += Math.hypot(a[i].x - b[i].x, a[i].y - b[i].y);
  }
  return sum / a.length;
}

const shapeTemplates = [
  {
    value: "0",
    points: [
      { x: 0.5, y: 0.06 },
      { x: 0.18, y: 0.18 },
      { x: 0.1, y: 0.5 },
      { x: 0.18, y: 0.84 },
      { x: 0.5, y: 0.96 },
      { x: 0.84, y: 0.84 },
      { x: 0.92, y: 0.5 },
      { x: 0.84, y: 0.18 },
      { x: 0.5, y: 0.06 },
    ],
  },
  {
    value: "1",
    points: [
      { x: 0.34, y: 0.18 },
      { x: 0.52, y: 0.02 },
      { x: 0.52, y: 0.98 },
    ],
  },
  {
    value: "1",
    points: [
      { x: 0.18, y: 0.82 },
      { x: 0.44, y: 0.55 },
      { x: 0.62, y: 0.1 },
      { x: 0.62, y: 0.95 },
    ],
  },
  {
    value: "2",
    points: [
      { x: 0.18, y: 0.18 },
      { x: 0.55, y: 0.02 },
      { x: 0.85, y: 0.22 },
      { x: 0.67, y: 0.48 },
      { x: 0.18, y: 0.88 },
      { x: 0.9, y: 0.88 },
    ],
  },
  {
    value: "3",
    points: [
      { x: 0.18, y: 0.12 },
      { x: 0.78, y: 0.12 },
      { x: 0.78, y: 0.38 },
      { x: 0.48, y: 0.5 },
      { x: 0.78, y: 0.62 },
      { x: 0.78, y: 0.88 },
      { x: 0.16, y: 0.88 },
    ],
  },
  {
    value: "5",
    points: [
      { x: 0.82, y: 0.12 },
      { x: 0.22, y: 0.12 },
      { x: 0.2, y: 0.45 },
      { x: 0.65, y: 0.48 },
      { x: 0.84, y: 0.68 },
      { x: 0.64, y: 0.9 },
      { x: 0.18, y: 0.88 },
    ],
  },
  {
    value: "4",
    points: [
      { x: 0.78, y: 0.08 },
      { x: 0.28, y: 0.58 },
      { x: 0.86, y: 0.58 },
      { x: 0.78, y: 0.08 },
      { x: 0.78, y: 0.94 },
    ],
  },
  {
    value: "6",
    points: [
      { x: 0.76, y: 0.12 },
      { x: 0.36, y: 0.24 },
      { x: 0.2, y: 0.58 },
      { x: 0.34, y: 0.86 },
      { x: 0.72, y: 0.86 },
      { x: 0.86, y: 0.62 },
      { x: 0.64, y: 0.46 },
      { x: 0.26, y: 0.56 },
    ],
  },
  {
    value: "7",
    points: [
      { x: 0.12, y: 0.12 },
      { x: 0.88, y: 0.12 },
      { x: 0.58, y: 0.52 },
      { x: 0.34, y: 0.92 },
    ],
  },
  {
    value: "8",
    points: [
      { x: 0.5, y: 0.08 },
      { x: 0.24, y: 0.2 },
      { x: 0.42, y: 0.48 },
      { x: 0.72, y: 0.28 },
      { x: 0.5, y: 0.08 },
      { x: 0.3, y: 0.56 },
      { x: 0.5, y: 0.92 },
      { x: 0.78, y: 0.74 },
      { x: 0.42, y: 0.48 },
    ],
  },
  {
    value: "9",
    points: [
      { x: 0.74, y: 0.5 },
      { x: 0.58, y: 0.16 },
      { x: 0.24, y: 0.18 },
      { x: 0.16, y: 0.44 },
      { x: 0.42, y: 0.6 },
      { x: 0.74, y: 0.5 },
      { x: 0.66, y: 0.92 },
      { x: 0.32, y: 0.96 },
    ],
  },
  {
    value: "a",
    points: [
      { x: 0.18, y: 0.92 },
      { x: 0.5, y: 0.08 },
      { x: 0.84, y: 0.92 },
      { x: 0.66, y: 0.52 },
      { x: 0.34, y: 0.52 },
    ],
  },
  {
    value: "z",
    points: [
      { x: 0.16, y: 0.16 },
      { x: 0.84, y: 0.16 },
      { x: 0.18, y: 0.88 },
      { x: 0.86, y: 0.88 },
    ],
  },
];

function scoreWithTemplates(points) {
  let best = { value: "?", score: Infinity };
  const candidates = shapeTemplates.filter((template) => /^[0-9]$/.test(template.value));
  candidates.forEach((template) => {
    const score = templateDistance(points, template.points);
    if (score < best.score) best = { value: template.value, score };
  });
  return best;
}

function strokeSlope(points) {
  const first = points[0];
  const last = points[points.length - 1];
  const dx = last.x - first.x;
  if (Math.abs(dx) < 1) return (last.y - first.y) * 99;
  return (last.y - first.y) / dx;
}

function boundsCenter(bounds) {
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
}

function boundsOverlapAmount(a, b) {
  return {
    x: Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX),
    y: Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY),
  };
}

function isFlatStroke(points) {
  const b = getBounds(points);
  return (b.maxY - b.minY) / Math.max(1, b.maxX - b.minX) < 0.28;
}

function isVerticalStroke(points) {
  const b = getBounds(points);
  return (b.maxX - b.minX) / Math.max(1, b.maxY - b.minY) < 0.38;
}

function isDiagonalStroke(points) {
  const b = getBounds(points);
  const width = Math.max(1, b.maxX - b.minX);
  const height = Math.max(1, b.maxY - b.minY);
  const lineDistance = Math.hypot(points[points.length - 1].x - points[0].x, points[points.length - 1].y - points[0].y);
  return width > 10 && height > 10 && lineDistance / Math.max(width, height) > 0.82;
}

function looksLikeX(strokes) {
  if (!Array.isArray(strokes) || strokes.length !== 2) return false;
  const [a, b] = strokes;
  if (!isDiagonalStroke(a) || !isDiagonalStroke(b)) return false;
  const slopeA = strokeSlope(a);
  const slopeB = strokeSlope(b);
  const boundsA = getBounds(a);
  const boundsB = getBounds(b);
  const overlapX = Math.min(boundsA.maxX, boundsB.maxX) - Math.max(boundsA.minX, boundsB.minX);
  const overlapY = Math.min(boundsA.maxY, boundsB.maxY) - Math.max(boundsA.minY, boundsB.minY);
  return slopeA * slopeB < 0 && overlapX > 0 && overlapY > 0;
}

function looksLikePlus(strokes) {
  if (!Array.isArray(strokes) || strokes.length !== 2) return false;
  const flat = strokes.find(isFlatStroke);
  const vertical = strokes.find(isVerticalStroke);
  if (!flat || !vertical) return false;
  const fb = getBounds(flat);
  const vb = getBounds(vertical);
  const overlap = boundsOverlapAmount(fb, vb);
  return overlap.x > -8 && overlap.y > -8 && boundsCenter(vb).x > fb.minX && boundsCenter(vb).x < fb.maxX;
}

function looksLikeMultiStrokeOne(strokes) {
  if (!Array.isArray(strokes) || strokes.length < 1 || strokes.length > 2) return false;
  const all = getBounds(strokes.flat());
  const width = Math.max(1, all.maxX - all.minX);
  const height = Math.max(1, all.maxY - all.minY);
  if (height < width * 1.15) return false;
  const hasMain = strokes.some((stroke) => isVerticalStroke(stroke) || (isDiagonalStroke(stroke) && Math.abs(strokeSlope(stroke)) > 1.2));
  return hasMain && width < height * 0.72;
}

function looksLikeMultiStrokeFive(strokes) {
  if (!Array.isArray(strokes) || strokes.length < 2 || strokes.length > 3) return false;
  const flat = strokes.find(isFlatStroke);
  if (!flat) return false;
  const flatBounds = getBounds(flat);
  const body = strokes.filter((s) => s !== flat).flat();
  if (body.length < 2) return false;
  const bodyBounds = getBounds(body);
  const overlap = boundsOverlapAmount(flatBounds, bodyBounds);
  const flatIsTop = flatBounds.maxY < bodyBounds.minY + (bodyBounds.maxY - bodyBounds.minY) * 0.35;
  return flatIsTop && overlap.x > -12;
}

function looksLikeFour(strokes) {
  if (!Array.isArray(strokes) || strokes.length < 2 || strokes.length > 3) return false;
  const flat = strokes.find(isFlatStroke);
  const vertical = strokes.find(isVerticalStroke);
  if (!flat || !vertical) return false;
  const all = getBounds(strokes.flat());
  const height = Math.max(1, all.maxY - all.minY);
  const flatBounds = getBounds(flat);
  const verticalBounds = getBounds(vertical);
  const flatCenterY = boundsCenter(flatBounds).y;
  const verticalCenterX = boundsCenter(verticalBounds).x;
  const hasLeftLeg = strokes.some((stroke) => {
    if (stroke === flat || stroke === vertical) return false;
    const b = getBounds(stroke);
    return b.minX < verticalCenterX && b.maxY > all.minY + height * 0.3;
  });

  return (
    flatCenterY > all.minY + height * 0.28 &&
    flatCenterY < all.minY + height * 0.72 &&
    verticalCenterX > all.minX + (all.maxX - all.minX) * 0.45 &&
    hasLeftLeg
  );
}

function looksLikeA(strokes) {
  if (!Array.isArray(strokes) || strokes.length < 2 || strokes.length > 3) return false;
  const diagonals = strokes.filter(isDiagonalStroke);
  if (diagonals.length < 2) return false;
  const slopes = diagonals.map(strokeSlope);
  const hasOppositeDiagonals = slopes.some((a) => slopes.some((b) => a * b < 0));
  if (!hasOppositeDiagonals) return false;
  if (strokes.length === 2) return true;
  return strokes.some(isFlatStroke);
}

function groupedStrokes(strokes) {
  const enriched = strokes
    .map((points) => ({ points, bounds: getBounds(points) }))
    .sort((a, b) => boundsCenter(a.bounds).x - boundsCenter(b.bounds).x);

  const groups = [];
  enriched.forEach((item) => {
    const last = groups[groups.length - 1];
    if (!last) {
      groups.push([item]);
      return;
    }

    const lastBounds = getBounds(last.flatMap((entry) => entry.points));
    const currentBounds = item.bounds;
    const lastWidth = Math.max(1, lastBounds.maxX - lastBounds.minX);
    const currentWidth = Math.max(1, currentBounds.maxX - currentBounds.minX);
    const gap = currentBounds.minX - lastBounds.maxX;
    const overlap = boundsOverlapAmount(lastBounds, currentBounds);
    const closeEnough = gap < Math.max(12, Math.min(lastWidth, currentWidth) * 0.45);
    const strongOverlap = overlap.x > -6;
    const flatInvolved = last.some((entry) => isFlatStroke(entry.points)) || isFlatStroke(item.points);
    const canJoin = strongOverlap || (closeEnough && last.length < 3 && flatInvolved);

    if (canJoin) last.push(item);
    else groups.push([item]);
  });

  return groups.map((group) => group.map((entry) => entry.points));
}

function groupedStrokeEntries(strokes) {
  const enriched = strokes
    .map((points) => ({ points, bounds: getBounds(points) }))
    .sort((a, b) => boundsCenter(a.bounds).x - boundsCenter(b.bounds).x);

  const groups = [];
  enriched.forEach((item) => {
    const last = groups[groups.length - 1];
    if (!last) {
      groups.push([item]);
      return;
    }

    const lastBounds = getBounds(last.flatMap((entry) => entry.points));
    const currentBounds = item.bounds;
    const lastWidth = Math.max(1, lastBounds.maxX - lastBounds.minX);
    const currentWidth = Math.max(1, currentBounds.maxX - currentBounds.minX);
    const gap = currentBounds.minX - lastBounds.maxX;
    const overlap = boundsOverlapAmount(lastBounds, currentBounds);
    const closeEnough = gap < Math.max(12, Math.min(lastWidth, currentWidth) * 0.45);
    const strongOverlap = overlap.x > -6;
    const flatInvolved = last.some((entry) => isFlatStroke(entry.points)) || isFlatStroke(item.points);
    const canJoin = strongOverlap || (closeEnough && last.length < 3 && flatInvolved);

    if (canJoin) last.push(item);
    else groups.push([item]);
  });

  return groups;
}

function recognizeStrokeGroup(group) {
  if (looksLikeFour(group)) return "4";
  if (looksLikeMultiStrokeFive(group)) return "5";
  if (looksLikeMultiStrokeOne(group)) return "1";

  if (group.length === 1) return recognizeSingleStroke(group[0]);

  const combined = group.flat();
  const best = scoreWithTemplates(combined);
  if (best.score < 0.46) return best.value;
  return "?";
}

function isSuperscriptGroup(current, previous) {
  if (!current || !previous) return false;
  if (!/^[0-9a-zA-Z]$/.test(current.value)) return false;
  if (!/^[0-9a-zA-Z)]$/.test(previous.value)) return false;

  const prevHeight = Math.max(1, previous.bounds.maxY - previous.bounds.minY);
  const currHeight = Math.max(1, current.bounds.maxY - current.bounds.minY);
  const currCenter = boundsCenter(current.bounds);

  const isHigh = currCenter.y < previous.bounds.minY + prevHeight * 0.38;
  const isSmaller = currHeight < prevHeight * 0.9;
  const startsNearRightHalf = current.bounds.minX > previous.bounds.minX + (previous.bounds.maxX - previous.bounds.minX) * 0.35;
  const notTooFar = current.bounds.minX < previous.bounds.maxX + prevHeight * 1.2;

  return isHigh && isSmaller && startsNearRightHalf && notTooFar;
}

function recognizeExpressionGroups(strokes) {
  const groups = groupedStrokeEntries(strokes).map((entries) => {
    const group = entries.map((entry) => entry.points);
    return {
      value: recognizeStrokeGroup(group),
      bounds: getBounds(entries.flatMap((entry) => entry.points)),
    };
  });

  let result = "";
  let lastBase = null;

  groups.forEach((group) => {
    if (isSuperscriptGroup(group, lastBase)) {
      result += `^${group.value}`;
      return;
    }

    result += group.value;
    if (group.value !== "+" && group.value !== "-" && group.value !== "/" && group.value !== "^") {
      lastBase = group;
    }
  });

  return result;
}

function recognizeSingleStroke(points) {
  if (!Array.isArray(points) || points.length < 2) return "";

  const b = getBounds(points);
  const width = Math.max(1, b.maxX - b.minX);
  const height = Math.max(1, b.maxY - b.minY);
  const start = points[0];
  const end = points[points.length - 1];
  const closed = Math.hypot(start.x - end.x, start.y - end.y) < Math.max(width, height) * 0.22;
  const flat = height / width < 0.22;
  const tall = width / height < 0.28;
  const lineDistance = Math.hypot(end.x - start.x, end.y - start.y);
  const mostlyStraight = lineDistance / Math.max(width, height) > 0.78;
  const mostlyVertical = height > width * 1.25;

  if (flat) return "-";
  if (closed && width / height > 0.45 && width / height < 1.6) return "0";
  if ((tall || (mostlyStraight && mostlyVertical)) && height > 24) return "1";

  const best = scoreWithTemplates(points);
  if (best.score < 0.55) return best.value;

  return "?";
}

function recognizeStrokes(strokes) {
  const usable = (Array.isArray(strokes) ? strokes : []).filter((s) => s.length > 1);
  if (!usable.length) return "";

  const enriched = usable.map((points) => ({ points, bounds: getBounds(points) }));
  const all = getBounds(usable.flat());
  const totalWidth = Math.max(1, all.maxX - all.minX);
  const totalHeight = Math.max(1, all.maxY - all.minY);

  const fractionBar = enriched.find(({ bounds }) => {
    const w = bounds.maxX - bounds.minX;
    const h = bounds.maxY - bounds.minY;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    return (
      w > totalWidth * 0.35 &&
      h < Math.max(8, totalHeight * 0.12) &&
      centerY > all.minY + totalHeight * 0.28 &&
      centerY < all.maxY - totalHeight * 0.18
    );
  });

  if (fractionBar) {
    const barY = (fractionBar.bounds.minY + fractionBar.bounds.maxY) / 2;
    const above = usable.filter((s) => s !== fractionBar.points && getBounds(s).maxY < barY);
    const below = usable.filter((s) => s !== fractionBar.points && getBounds(s).minY > barY);
    const top = recognizeStrokes(above);
    const bottom = recognizeStrokes(below);
    if (top && bottom && !top.includes("?") && !bottom.includes("?")) return `${top}/${bottom}`;
  }

  return groupedStrokes(usable).map(recognizeStrokeGroup).join("");
}

function normalizeHandwritingText(text) {
  return String(text || "").replace(/\s+/g, "");
}

function handwritingLatexPreview(text) {
  const normalized = normalizeHandwritingText(text);
  if (!normalized) return "";
  return normalized
    .replace(/\*/g, "\\cdot ")
    .replace(/([a-zA-Z0-9])\^([a-zA-Z0-9]+)/g, "$1^{$2}")
    .replace(/^(.+?)\/(.+)$/, "\\dfrac{$1}{$2}");
}

function HandwritingPanel({ onInsert, gap }) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const drawingRef = useRef(false);
  const [recognized, setRecognized] = useState("");
  const [status, setStatus] = useState("Napisz odpowiedz, potem kliknij Rozpoznaj.");

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(5, 8, 35, .58)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = "rgba(148, 163, 184, .22)";
    ctx.lineWidth = 1;
    for (let x = 24; x < canvas.width; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 24; y < canvas.height; y += 24) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    ctx.strokeStyle = "#f8fafc";
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    strokesRef.current.forEach((stroke) => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      stroke.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  }, []);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const recognize = () => {
    const next = recognizeStrokes(strokesRef.current);
    setRecognized(next);
    setStatus(
      next && !next.includes("?")
        ? "Rozpoznano roboczo. Mozesz poprawic tekst przed wstawieniem."
        : "Niepewne rozpoznanie - popraw tekst recznie albo napisz wiekszym pismem."
    );
  };

  const clear = () => {
    strokesRef.current = [];
    setRecognized("");
    setStatus("Wyczyszczono. Napisz odpowiedz od nowa.");
    redraw();
  };

  const normalized = normalizeHandwritingText(recognized);
  const preview = handwritingLatexPreview(recognized);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      <canvas
        ref={canvasRef}
        width={520}
        height={180}
        style={{
          width: "100%",
          height: 170,
          borderRadius: 12,
          touchAction: "none",
          border: "1px solid rgba(148, 163, 184, .35)",
          cursor: "crosshair",
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drawingRef.current = true;
          strokesRef.current = [...strokesRef.current, [pointFromEvent(event)]];
          redraw();
        }}
        onPointerMove={(event) => {
          if (!drawingRef.current) return;
          const strokes = strokesRef.current;
          strokes[strokes.length - 1].push(pointFromEvent(event));
          redraw();
        }}
        onPointerUp={(event) => {
          drawingRef.current = false;
          event.currentTarget.releasePointerCapture?.(event.pointerId);
        }}
        onPointerCancel={() => {
          drawingRef.current = false;
        }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700 }}>
          Rozpoznano jako:
          <input
            className="input-secondary"
            value={recognized}
            onChange={(event) => setRecognized(event.target.value)}
            placeholder="tu pojawi sie tekst, np. x^2, 11, -2, 1/2"
            style={{ width: "100%", minWidth: 0 }}
          />
        </label>
        {recognized && (
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            <span>Podglad: </span>
            <InlineMath math={preview || normalized} />
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap }}>
          <button type="button" className="btn-secondary" onClick={recognize}>
            Rozpoznaj
          </button>
          <button type="button" className="btn-secondary" onClick={() => onInsert(normalized)} disabled={!recognized}>
            Wstaw
          </button>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap, alignItems: "center", fontSize: 12, opacity: 0.86 }}>
        <span>{status}</span>
        <button type="button" className="btn-secondary" onClick={clear} style={{ padding: "6px 10px" }}>
          Wyczysc
        </button>
      </div>
    </div>
  );
}

export default function SetKeyboard({
  targetRef,
  onAction,
  open,
  defaultOpen = false,
  onOpenChange,
  className = "",
  style,
  compact = false,
  render = "both",
  panelIndent = 0,
  singleOpen = true,
  group = "default",
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [panelMode, setPanelModeState] = useState("keyboard");
  const isControlled = typeof open === "boolean";
  const actuallyOpen = isControlled ? open : internalOpen;
  const handwritingEnabled = false;

  const uidRef = useRef(Math.random().toString(36).slice(2));
  const groupName = group || "default";
  const rootRef = useRef(null);
  const anchorRef = useRef(undefined);

  const resolveEl = useCallback(() => {
    return (targetRef && targetRef.current) || findAnchorElement(rootRef.current);
  }, [targetRef]);

  const refreshAnchor = useCallback(() => {
    const el = resolveEl();
    anchorRef.current = el ? getAnchorId(el) : undefined;
  }, [resolveEl]);

  useEffect(() => {
    refreshAnchor();
  }, [refreshAnchor]);

  useEffect(() => {
    const t = setTimeout(refreshAnchor, 0);
    return () => clearTimeout(t);
  }, [refreshAnchor]);

  const setOpen = useCallback(
    (next) => {
      if (isControlled) onOpenChange && onOpenChange(next);
      else {
        setInternalOpen(next);
        onOpenChange && onOpenChange(next);
      }
    },
    [isControlled, onOpenChange]
  );

  const setPanelMode = useCallback(
    (mode) => {
      const key = getModeKey(groupName, anchorRef.current);
      modeRegistry.set(key, mode);
      setPanelModeState(mode);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: { key, mode } }));
      }
    },
    [groupName]
  );

  useEffect(() => {
    if (!handwritingEnabled) {
      setPanelModeState("keyboard");
      return;
    }
    const key = getModeKey(groupName, anchorRef.current);
    const stored = modeRegistry.get(key);
    if (stored) setPanelModeState(stored);
  }, [groupName, actuallyOpen, handwritingEnabled]);

  useEffect(() => {
    const handler = (event) => {
      if (!handwritingEnabled) return;
      const key = getModeKey(groupName, anchorRef.current);
      if (event.detail?.key === key) setPanelModeState(event.detail.mode || "keyboard");
    };
    window.addEventListener(MODE_EVENT, handler);
    return () => window.removeEventListener(MODE_EVENT, handler);
  }, [groupName, handwritingEnabled]);

  const claimGroup = useCallback(() => {
    if (!singleOpen) return;
    const el = resolveEl();
    const currentAnchor = el ? getAnchorId(el) : anchorRef.current;
    anchorRef.current = currentAnchor;
    const prev = openRegistry.get(groupName);
    const sameKnownAnchor = prev && prev.anchor && currentAnchor && prev.anchor === currentAnchor;
    const bothAnchorsUnknown = prev && prev.uid !== uidRef.current && !prev.anchor && !currentAnchor;

    if (prev && prev.uid !== uidRef.current && !sameKnownAnchor && !bothAnchorsUnknown) {
      prev.close?.();
    }

    openRegistry.set(groupName, {
      uid: uidRef.current,
      anchor: currentAnchor,
      close: () => setOpen(false),
    });
  }, [groupName, singleOpen, setOpen, resolveEl]);

  useEffect(() => {
    if (actuallyOpen) {
      claimGroup();
    } else {
      const cur = openRegistry.get(groupName);
      if (cur && cur.uid === uidRef.current) openRegistry.delete(groupName);
    }
  }, [actuallyOpen, claimGroup, groupName]);

  const getEl = useCallback(() => resolveEl(), [resolveEl]);

  const insertAtCursor = useCallback(
    (text) => {
      const el = getEl();
      if (!el) return false;
      el.focus();
      const { selectionStart: s = el.value.length, selectionEnd: e = el.value.length, value = "" } = el;
      const next = value.slice(0, s) + text + value.slice(e);
      el.value = next;
      const pos = s + text.length;
      el.setSelectionRange(pos, pos);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    },
    [getEl]
  );

  const doBackspace = useCallback(() => {
    const el = getEl();
    if (!el) return false;
    el.focus();
    const { selectionStart: s = 0, selectionEnd: e = 0, value = "" } = el;
    if (s !== e) {
      el.value = value.slice(0, s) + value.slice(e);
      el.setSelectionRange(s, s);
    } else if (s > 0) {
      el.value = value.slice(0, s - 1) + value.slice(e);
      el.setSelectionRange(s - 1, s - 1);
    }
    el.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }, [getEl]);

  const moveCursor = useCallback(
    (dir) => {
      const el = getEl();
      if (!el) return false;
      el.focus();
      const { selectionStart: s = 0, selectionEnd: e = 0, value = "" } = el;
      const pos = dir === "left" ? Math.max(0, Math.min(s, e) - 1) : Math.min(value.length, Math.max(s, e) + 1);
      el.setSelectionRange(pos, pos);
      return true;
    },
    [getEl]
  );

  const doClear = useCallback(() => {
    const el = getEl();
    if (!el) return false;
    el.value = "";
    el.setSelectionRange(0, 0);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }, [getEl]);

  const defaultAction = useCallback(
    (type, payload) => {
      switch (type) {
        case "insert":
          return insertAtCursor(String(payload ?? ""));
        case "backspace":
          return doBackspace();
        case "moveLeft":
          return moveCursor("left");
        case "moveRight":
          return moveCursor("right");
        case "clear":
          return doClear();
        case "toggle": {
          setPanelMode("keyboard");
          if (!actuallyOpen) {
            claimGroup();
            setOpen(true);
          } else {
            setOpen(false);
          }
          return true;
        }
        default:
          return false;
      }
    },
    [insertAtCursor, doBackspace, moveCursor, doClear, setPanelMode, actuallyOpen, claimGroup, setOpen]
  );

  const act = useCallback(
    (type, payload) => {
      if (onAction) onAction(type, payload);
      else defaultAction(type, payload);
    },
    [onAction, defaultAction]
  );

  const gap = compact ? 6 : 8;
  const btnBase = useMemo(
    () => ({
      padding: "10px 8px",
      borderRadius: 12,
      fontSize: 14,
      lineHeight: 1.1,
      fontWeight: 600,
      transition: "transform .06s ease, box-shadow .15s ease",
      outline: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    []
  );

  const ToggleBtn = ({ active }) => (
    <button
      type="button"
      onClick={() => defaultAction("toggle")}
      title={actuallyOpen && panelMode === "keyboard" ? "Ukryj klawiature" : "Pokaz klawiature"}
      aria-pressed={active}
      className="btn-secondary"
      style={{
        width: 28,
        height: 28,
        minWidth: 28,
        minHeight: 28,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 0,
        lineHeight: 0,
        padding: 0,
        boxShadow: active ? "0 0 0 3px rgba(255,184,77,.45) inset, 0 0 18px rgba(255,184,77,.25)" : undefined,
        transition: "box-shadow .15s ease",
      }}
      data-open={active ? "true" : "false"}
    >
      <span style={{ color: "currentColor" }}>
        <KeyboardIcon size={16} />
      </span>
    </button>
  );

  const HandwritingToggleBtn = ({ active }) => (
    <button
      type="button"
      onClick={() => {
        setPanelMode("handwriting");
        if (!actuallyOpen) {
          claimGroup();
          setOpen(true);
        } else if (panelMode === "handwriting") {
          setOpen(false);
        }
      }}
      title={active ? "Ukryj pisanie reczne" : "Pisz recznie"}
      aria-pressed={active}
      className="btn-secondary"
      style={{
        width: 28,
        height: 28,
        minWidth: 28,
        minHeight: 28,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 0,
        lineHeight: 0,
        padding: 0,
        boxShadow: active ? "0 0 0 3px rgba(45,212,191,.45) inset, 0 0 18px rgba(45,212,191,.25)" : undefined,
        transition: "box-shadow .15s ease",
      }}
      data-open={active ? "true" : "false"}
    >
      <span style={{ color: "currentColor" }}>
        <HandwritingIcon size={16} />
      </span>
    </button>
  );

  const KeyBtn = ({ label, onClick, colSpan = 1, style: btnStyle, ariaLabel }) => (
    <button
      type="button"
      onClick={onClick}
      className="btn-secondary"
      aria-label={ariaLabel}
      style={{ ...btnBase, gridColumn: `span ${colSpan}`, ...btnStyle }}
    >
      {label}
    </button>
  );

  const insert = (s) => () => act("insert", s);

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ ...style, display: "inline-flex", flexDirection: "column", gap: 6 }}
      data-kbd-open={actuallyOpen ? "true" : "false"}
      data-kbd-group={groupName}
    >
      {(render === "both" || render === "toggle") && (
        <div style={{ display: "flex", gap: 6 }}>
          <ToggleBtn active={actuallyOpen && panelMode === "keyboard"} />
          {handwritingEnabled && <HandwritingToggleBtn active={actuallyOpen && panelMode === "handwriting"} />}
        </div>
      )}

      {(render === "both" || render === "panel") && (
        <div
          style={{
            width: "100%",
            overflow: "hidden",
            transition: "max-height .28s ease, opacity .22s ease, transform .28s ease",
            maxHeight: actuallyOpen ? 1000 : 0,
            opacity: actuallyOpen ? 1 : 0,
            transform: `translateY(${actuallyOpen ? "0" : "-6px"})`,
          }}
        >
          <div
            style={{
              background: "linear-gradient(180deg, rgba(90,120,255,.16), rgba(60,90,255,.14))",
              backdropFilter: "blur(8px) saturate(1.1)",
              WebkitBackdropFilter: "blur(8px) saturate(1.1)",
              border: "1px solid rgba(120,150,255,.35)",
              boxShadow: "0 8px 24px rgba(60,90,255,.18)",
              borderRadius: 14,
              padding: gap,
              marginTop: 6,
              marginLeft: panelIndent,
            }}
          >
            {handwritingEnabled && (
              <div style={{ display: "flex", gap, marginBottom: gap }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPanelMode("keyboard")}
                  style={{
                    padding: "7px 10px",
                    fontSize: 12,
                    boxShadow: panelMode === "keyboard" ? "0 0 0 2px rgba(255,184,77,.35) inset" : undefined,
                  }}
                >
                  Klawiatura
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPanelMode("handwriting")}
                  style={{
                    padding: "7px 10px",
                    fontSize: 12,
                    boxShadow: panelMode === "handwriting" ? "0 0 0 2px rgba(45,212,191,.35) inset" : undefined,
                  }}
                >
                  Pisanie reczne
                </button>
              </div>
            )}

            {handwritingEnabled && panelMode === "handwriting" ? (
              <HandwritingPanel onInsert={(text) => act("insert", text)} gap={gap} />
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap }}>
                <KeyBtn label="1" onClick={insert("1")} />
                <KeyBtn label="2" onClick={insert("2")} />
                <KeyBtn label="3" onClick={insert("3")} />
                <KeyBtn label="4" onClick={insert("4")} />
                <KeyBtn label="5" onClick={insert("5")} />

                <KeyBtn label="6" onClick={insert("6")} />
                <KeyBtn label="7" onClick={insert("7")} />
                <KeyBtn label="8" onClick={insert("8")} />
                <KeyBtn label="9" onClick={insert("9")} />
                <KeyBtn label="0" onClick={insert("0")} />

                <KeyBtn label="+" onClick={insert("+")} />
                <KeyBtn label="-" onClick={insert("-")} />
                <KeyBtn label="*" onClick={insert("*")} ariaLabel="mnozenie" />
                <KeyBtn label="a/b" onClick={insert("/")} ariaLabel="dzielenie" />
                <KeyBtn label="a^n" onClick={insert("^")} ariaLabel="potega" />

                <KeyBtn label="x" onClick={insert("x")} />
                <KeyBtn label="y" onClick={insert("y")} />
                <KeyBtn label="z" onClick={insert("z")} />
                <KeyBtn label="<-" onClick={() => act("moveLeft")} ariaLabel="kursor w lewo" />
                <KeyBtn label="->" onClick={() => act("moveRight")} ariaLabel="kursor w prawo" />

                <KeyBtn label="(" onClick={insert("(")} ariaLabel="nawias otwierajacy" />
                <KeyBtn label=")" onClick={insert(")")} ariaLabel="nawias zamykajacy" />
                <KeyBtn label={<SpaceIcon />} onClick={insert(" ")} ariaLabel="spacja" colSpan={2} />
                <KeyBtn label="," onClick={insert(",")} ariaLabel="przecinek" />

                <KeyBtn label="Bksp" onClick={() => act("backspace")} ariaLabel="kasuj znak" colSpan={2} />
                <KeyBtn label="Clear" onClick={() => act("clear")} ariaLabel="wyczysc wszystko" colSpan={2} />
                <KeyBtn label="." onClick={insert(".")} ariaLabel="kropka" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
